import * as React from 'react';
import { ApolloQueryResult, QueryResult } from '@apollo/client';
import { defaultTo, intersectionBy, isEmpty } from 'lodash';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import set from 'lodash/set';
import startCase from 'lodash/startCase';
import { Link } from 'react-router-dom';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { ErrorMessage, Field, FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Select, { ValueType } from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { PricingPlanCycle, PricingPlanType as PricingType } from '@atlassian/cssxp-ccp-types';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import { InvoiceGroup, Maybe, Offering, Order, OrderLineItem, SyntheticPricingPlan } from '@css-xp/graphtypes';
import {
  EntitlementDetails,
  FetchAvailableOfferingsForEntitlementQuery,
  FetchOrdersForRewindOnEntitlementQuery,
  Promotion,
} from '@css-xp/graphtypes/src/generated-types-new';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { logTimeStamp } from '@atlassian/temp-metal';
import { fetchRewindOrderDetails, getBillEstimate, updateEntitlementPlan } from '@atlassian/temp-proxies';
import { OMSMetadata, OrderItemType } from '@atlassian/cssxp-types-entitlement';
import {
  INVOICE_PATH_2,
  InfoMessage,
  NEW_DATE_FORMAT,
  OfferingName,
  PricingPlanStatus,
  PricingPlanType,
  PromotionInstance,
  UpdateOrderRequest,
} from '@atlassian/cssxp-typescommon';
import { NOT_AVAILABLE } from '@atlassian/cssxp-utils-entitlement';
import { ENABLE_ORDER_BILL_ESTIMATION } from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { ApprovalEntityInput, ApprovalType, EntityType, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { ApprovalFormSection, useApprovals } from '@css-xp/apd-process-manager';
import { TaskDetailAction, useCancelRequest, useTaskList } from '@css-xp/apd-task-manager';
import {
  EntitlementIcon,
  ErrorComponents,
  FormattedDate,
  InflightInvoiceBanner,
  InfoPanel,
  InvoiceGroupFormField,
  NotificationTypes,
  OrderSelectLineItem,
  SelectOrderFormFieldV2,
  SummarySectionV2,
  isDiscretionaryDiscountPromotion,
  orderSuccessNotification,
  successNotificationBuilder,
  useFeatureToggle,
  useFormSubmit,
  useNotification,
  useSidebar,
  withApollo,
} from '@css-xp/base';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import { PromotionItemDetail, SelectPromotionFormFieldV2, SelectPromotionSelectItemV2 } from '@css-xp/base/forms';
import { fetchAvailableOfferingsForEntitlement } from '../../../../entitlement-page/src/queries/fetch-available-offerings-for-entitlement';
import { fetchInvoiceForOrderToCancel } from '../../../../entitlement-page/src/queries/fetch-invoice-for-order-to-cancel';
import { fetchOrdersForRewind } from '../../../../entitlement-page/src/queries/fetch-rewind-orders';
import { getCustomPromotionInstance } from '../../utils/promotion-utils';
import './entitlement-rewind-and-rebill-pane.scss';

interface Props {
  id: string;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountContext: TransactionAccountContextType;
  promotions: Promotion[];
  appliedPromotions: Promotion[];
  billingGroupIdOnEntitlement: string;
  billingFrequencyOnEntitlement: string;
  entitlement: EntitlementDetails;
}

interface InvoiceGroupFormField {
  invoiceGroup: InvoiceGroup;
  value: string;
}

interface OrderFormField {
  order: Order;
  value: string;
}

interface OfferingSelectItem {
  label: string;
  value: {
    id: string;
  };
}

export enum OrderType {
  CANCELLATION_ORDER,
  SYSTEM_CANCELLATION_ORDER,
  ACCOUNT_MODIFICATION_ORDER,
  REWIND_ORDER,
  REACTIVATION_ORDER,
  CREATION_ORDER,
}

export enum OrderTransitionType {
  SCHEDULED,
  SCHEDULE,
}

interface CustomValues {
  value: string;
  label: string;
}
interface FormData {
  billingFrequency?: string;
  invoiceGroupId?: InvoiceGroupFormField;
  offeringSelectedItem?: OfferingSelectItem;
  order?: OrderFormField;
  pricingPlan?: string;
  productId?: string;
  promotionId?: SelectPromotionSelectItemV2;
  offeringQuantity?: string;
  supportTicketNumber?: string | null;
  handNote?: string | null;
  customDuration?: CustomValues[];
  customIterations?: string[] | null[];
  customReason?: CustomValues[];
  customValue?: string[] | number[];
  comments?: string;
}

interface PricingOptionsType {
  name?: string;
  value?: string;
  label?: string;
  isDisabled?: boolean;
  testId?: string;
}

type EstimateBill = {
  response?: any;
  error?: any;
  loading?: boolean;
};

const CommercialPlan: SyntheticPricingPlan[] = [
  {
    type: 'COMMERCIAL',
    status: PricingPlanStatus.ACTIVE,
    discountPercentage: 0,
  },
];

const getDiscreteDiscountPromotions = (formData: FormData, formApi: FormApi<FormData>) => {
  const transformedData: any = {};
  formApi.getRegisteredFields().forEach((key: any) => {
    const value = formApi.getFieldState(key)?.value ?? get(formData, key);
    set(transformedData, key, value);
  });
  const promotions: any[] = [];
  const promotionKeys = Object.keys(transformedData).filter((key) => key.trim().includes('promotionId'));
  promotionKeys?.forEach((key: string, index: number) => {
    if (isDiscretionaryDiscountPromotion(transformedData, key)) {
      promotions.push(getCustomPromotionInstance(index, formData));
    }
  });
  return promotions;
};

export const EntitlementRewindAndRebillPaneComponent = ({
  id,
  promotions,
  transactionAccountContext,
  appliedPromotions = [],
  billingGroupIdOnEntitlement,
  billingFrequencyOnEntitlement,
  entitlement,
  refetch,
}: Props) => {
  // hook to submit order
  const startTime = new Date().getTime();
  const {
    error: formError,
    isProcessing: processingOrder,
    submitForm: submitRewindOrder,
    complete: rewindOrderComplete,
    data: rewindResponse,
  } = useFormSubmit<UpdateOrderRequest, any>({
    submitFormAction: updateEntitlementPlan,
  });

  const { closeSidebar } = useSidebar();
  const [defaultPricePlan, setDefaultPricePlan] = React.useState<string | undefined>('');
  const [ordersForRewind, setOrdersForRewind] = React.useState<
    Partial<QueryResult<FetchOrdersForRewindOnEntitlementQuery>>
  >({ loading: true });
  const [availableOfferingsData, setAvailableOfferings] = React.useState<
    Partial<QueryResult<FetchAvailableOfferingsForEntitlementQuery>>
  >({ loading: true });
  const [selectedOrderLineItem, setSelectedOrderLineItem] = React.useState<OrderLineItem | null>();
  const [defaultOfferingItem, setDefaultOfferingItem] = React.useState<OfferingSelectItem | undefined>({
    label: '',
    value: {
      id: '',
    },
  });
  const [rewindOrderDetails, setRewindOrderDetails] = React.useState<any>({ loading: false, data: null, error: null });
  const [defaultOfferingQuantity, setDefaultOfferingQuantity] = React.useState<string>('1');
  const [isUserTierDisabled, setIsUserTierDisabled] = React.useState(true);
  const [selectedInvoiceGroupPricingPlan, setSelectedInvoiceGroupPricingPlan] = React.useState<
    string | undefined | null
  >('');
  const [invoiceData, setInvoiceData] = React.useState<any>(null);
  const [customDiscount, setCustomDiscount] = React.useState<number>(0);
  const [estimatedBill, setEstimatedBill] = React.useState<Partial<EstimateBill>>({ loading: false });
  const [isEnabled] = useFeatureToggle();
  const isBillEsimationEnabled = isEnabled(ENABLE_ORDER_BILL_ESTIMATION);
  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const onApprovalComplete = React.useCallback(() => {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('approval-submit-success')
      .withHeader('Approval request submitted')
      .withMessage('You can view your request in the Approval requests section.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch?.();
  }, [closeSidebar, refetch, sendSuccessNotification, setNotificationSent]);

  const { isDiscountApprovalFlowEnabled } = useApprovalDeskPermissions();

  const {
    cancelApprovalRequestError,
    cancelApprovalRequestProcessing,
    submitRequestAfterApprovalCancelation,
  } = useCancelRequest({
    businessKey: entitlement.entitlementNumber,
  });

  const { hasOpenApprovalRequest, error: taskListError, data: taskListData } = useTaskList({
    businessKey: entitlement.entitlementNumber,
    skip: !entitlement.entitlementNumber || !isDiscountApprovalFlowEnabled,
  });

  const {
    approvalRequired,
    approvalError,
    approvalList,
    approvalSlackChannel,
    submitApproval,
    approvalProcessing,
    evaluate: evaluateApproval,
    setApprovalRequired,
  } = useApprovals({
    approvalType: ApprovalType.ORDER_DISCOUNT,
    onApprovalComplete,
  });

  React.useEffect(() => {
    (async () => {
      const orders = await graphqlClient().query({
        query: fetchOrdersForRewind,
        variables: { id },
        fetchPolicy: 'no-cache',
      });

      setOrdersForRewind({ ...orders });
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      const offerings = await graphqlClient().query({
        query: fetchAvailableOfferingsForEntitlement,
        variables: { id },
        fetchPolicy: 'no-cache',
      });

      setAvailableOfferings({ ...offerings });
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      if (selectedOrderLineItem) {
        setInvoiceData(null);
        const result = await graphqlClient().query({
          query: fetchInvoiceForOrderToCancel,
          variables: { id: selectedOrderLineItem?.lineItemId },
          fetchPolicy: 'no-cache',
        });
        setInvoiceData(result?.data?.invoiceForOrderToCancel);
      }
    })();
  }, [selectedOrderLineItem]);

  if (ordersForRewind.loading || availableOfferingsData.loading) return <LoadingIndicator />;
  if (ordersForRewind.error) {
    return <ErrorComponents.GenericErrorPage error={ordersForRewind.error as Error} />;
  }

  const orders = get(ordersForRewind, 'data.entitlementDetails.ordersForRewind.nodes', []) as Order[];
  const filteredOrderForRewind = orders.filter(
    (order) =>
      !Object.keys(OrderType).includes(order?.orderLineItems?.nodes?.[0]?.type as string) &&
      !Object.keys(OrderTransitionType).includes(order?.orderLineItems?.nodes?.[0]?.orderTransitionType as string) &&
      order?.orderLineItems?.nodes?.[0]?.pricingPlan?.billingFrequency === billingFrequencyOnEntitlement
  );

  const { data, getTransactionAccount } = transactionAccountContext;

  const txaccount = getTransactionAccount(data);
  const availableInvoiceGroups = (txaccount &&
    txaccount.availableInvoiceGroups &&
    txaccount.availableInvoiceGroups.nodes) as InvoiceGroup[];

  const availableOfferings = get(
    availableOfferingsData,
    'data.entitlementDetails.availableOfferings',
    []
  ) as Offering[];
  const appliedOffering = get(availableOfferingsData, 'data.entitlementDetails.offering', []);

  const availableOfferingOptions = availableOfferings?.map((offering: Offering) => ({
    label: offering.name,
    value: {
      id: offering.id,
    },
  })) as OfferingSelectItem[];

  if (appliedOffering) {
    availableOfferingOptions.unshift({
      label: appliedOffering.name,
      value: {
        id: appliedOffering.id,
      },
    });
  }
  const hasSingleInvoiceGroup = availableInvoiceGroups?.length === 1;

  const getPricingEligibility = (
    pricingOptions: PricingOptionsType[],
    selectedInvoiceGroupPricingPlanValue: string | null | undefined
  ) => {
    let eligiblePricingPlan = null;
    const offeringPricingPlans = pricingOptions.map((pricingOption: PricingOptionsType) => pricingOption.value);
    const txAccountPricingPlan = (txaccount?.billToPartyPricingEligibilty ?? '')?.toUpperCase();
    const invoiceGroupPricingPlan = (selectedInvoiceGroupPricingPlanValue ?? '')?.toUpperCase();

    // set fianl pricing plan to txa pricing plan -
    // if txa pricing plan is set and not equal to COMMERCIAL and offering pricing plan includes txa pricing plan
    // if no txa account and invoice group pricing plan is set check if it exist in offering pricing plan
    // else always set it to COMMERCIAL
    if (
      invoiceGroupPricingPlan &&
      invoiceGroupPricingPlan !== PricingPlanType.COMMERCIAL &&
      offeringPricingPlans.includes(invoiceGroupPricingPlan)
    ) {
      eligiblePricingPlan = invoiceGroupPricingPlan;
    } else if (
      txAccountPricingPlan &&
      txAccountPricingPlan !== PricingPlanType.COMMERCIAL &&
      offeringPricingPlans.includes(txAccountPricingPlan)
    ) {
      eligiblePricingPlan = txAccountPricingPlan;
    } else {
      eligiblePricingPlan = PricingPlanType.COMMERCIAL;
    }

    return eligiblePricingPlan;
  };

  const setDefaultPricingPlanDetails = (
    pricingOptions: PricingOptionsType[],
    selectedInvoiceGroupPricingPlanValue: string | null | undefined
  ) => {
    const selectedPricingPlan = getPricingEligibility(pricingOptions, selectedInvoiceGroupPricingPlanValue);
    const defaultPricingPlanType = '';
    // If only 1 synthetic pricing plan is available, then selecting it as default value.
    const getDefaultPricingPlan =
      pricingOptions.length === 1
        ? pricingOptions[0]
        : pricingOptions.find((pricingPlan) => capitalize(pricingPlan.value) === capitalize(defaultPricingPlanType));

    const defaultPricingPlan = selectedPricingPlan ?? get(getDefaultPricingPlan, 'value', '');
    setDefaultPricePlan(defaultPricingPlan);
  };

  const setSyntheticPricingOptions = (availablePricingPlanForOffering: SyntheticPricingPlan[]) => {
    const pricingOptions = availablePricingPlanForOffering.map((plan, index) => {
      return {
        name: plan.type,
        value: plan.type,
        label: `${startCase(get(plan, 'type', ''))} ${
          plan?.discountPercentage >= 0 && `(${plan?.discountPercentage}% off)`
        }`,
        isDisabled: plan.status !== PricingPlanStatus.ACTIVE,
        testId: `pricing-plan-grp-${index}`,
      };
    });

    return pricingOptions;
  };

  const onOfferingChange = (
    selectedItem: OfferingSelectItem | undefined,
    defaultInvoiceGroupPricingPlan: string | null | undefined
  ) => {
    const selectedId = get(selectedItem, 'value.id', '');
    const selectedName = get(selectedItem, 'label', '');
    const availablePricingPlanForOffering = ((selectedId === (appliedOffering && appliedOffering.id)
      ? appliedOffering && appliedOffering.availableSyntheticPricingPlans
      : availableOfferings.find((offering) => offering.id === selectedId)?.availableSyntheticPricingPlans) ??
      []) as SyntheticPricingPlan[];
    if (intersectionBy(availablePricingPlanForOffering, CommercialPlan, 'type').length === 0) {
      availablePricingPlanForOffering.push(CommercialPlan[0]);
    }

    //Enable user-tier/quantity for Audience-Specific SP offering.
    if (selectedName === OfferingName.AUDIENCE_SPECIFIC) {
      setIsUserTierDisabled(false);
    } else {
      setDefaultOfferingQuantity('1');
      setIsUserTierDisabled(true);
    }

    const pricingOptions = setSyntheticPricingOptions(availablePricingPlanForOffering);

    setDefaultPricingPlanDetails(pricingOptions, defaultInvoiceGroupPricingPlan);
  };

  const constructMetadata = (formData: FormData) => {
    const metadata: Record<string, string> = {};

    if (formData.handNote) {
      metadata[OMSMetadata.HAND_NOTE] = formData.handNote;
    }

    if (formData.supportTicketNumber) {
      metadata[OMSMetadata.SUPPORT_TICKET_NUMBER] = formData.supportTicketNumber;
    }

    return metadata;
  };

  const constructRequestData = (id: string, formData: FormData, formApi: FormApi<FormData>) => {
    const transformedData: any = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(formData, key);
      set(transformedData, key, value);
    });

    const promotions = Object.keys(transformedData).filter((key) => key.trim().includes('promotionId'));

    const promotionsToAdd: any[] = [];
    promotions?.forEach((key: string, index: number) => {
      // Do not add a promotion id (instead add custom promotion id) for any discretionary/custom promotion
      const promotionToAdd = !isDiscretionaryDiscountPromotion(transformedData, key)
        ? {
            promotionInstanceId: null,
            promotionDefinition: {
              promotionId: transformedData[key]?.value,
              customisedValues: null,
            },
          }
        : getCustomPromotionInstance(index, formData);

      promotionToAdd && promotionsToAdd.push(promotionToAdd);
    });

    const filteredPromotionsToAdd = promotionsToAdd.filter(
      // Handles the case if discretionary discount was selected but not selected any custom promotion
      (promotionToAdd) =>
        promotionToAdd.promotionDefinition.promotionId || promotionToAdd.promotionDefinition.customisedValues
    );

    const promotionIdsToAdd = filteredPromotionsToAdd
      .map((promotion) => promotion.promotionDefinition.promotionId)
      .filter((promo) => !!promo);

    const promotionsToRemove: any[] = [];
    appliedPromotions?.forEach((promotion: Maybe<Promotion>) => {
      const promotionId = promotion?.id;

      if (promotionId && !promotionIdsToAdd.includes(promotionId)) {
        promotionsToRemove.push({
          promotionDefinition: {
            promotionId: promotionId,
          },
        });
      }
    });

    //send rewindToOrderItemId if available,
    //else send selected orderItem Id.
    const getRewindToId = get(rewindOrderDetails, 'data.item.rewindToOrderItemId', selectedOrderLineItem?.lineItemId);

    const requestData: UpdateOrderRequest = {
      offeringId: get(defaultOfferingItem, 'value.id', ''),
      billingFrequency: entitlement?.billingFrequency || '',
      invoiceGroupId: get(transformedData, 'invoiceGroupId.value', ''),
      entitlementId: id,
      rewindTo: getRewindToId,
      pricingPlanType: defaultPricePlan,
      units: defaultOfferingQuantity,
      metadata: constructMetadata(formData),
      addPromotions: filteredPromotionsToAdd,
      removePromotions: promotionsToRemove,
      isPromotionStackingAllowed: true,
      productId: entitlement.productId ?? '', //@ts-ignore
      chargeQuantities: entitlement?.scheduledChanges?.chargeQuantities || [],
    };

    return requestData;
  };

  const handleFormSubmit = async (formData: FormData, formApi: FormApi<FormData>) => {
    logTimeStamp(startTime, 'Rewind', SignalFxFEPerfStore.FE_USER_TASK_REWIND_TIME);

    const requestData = constructRequestData(id, formData, formApi);
    const discreteDiscountPromotions = getDiscreteDiscountPromotions(formData, formApi);
    const promotionIds: PromotionInstance[] =
      requestData.addPromotions?.filter((value) => !value.promotionDefinition?.customisedValues) ?? [];
    const customPromotions: PromotionInstance[] =
      requestData.addPromotions?.filter((value) => !!value.promotionDefinition?.customisedValues) ?? [];

    const entity: ApprovalEntityInput = {
      entityType: EntityType.ENTITLEMENT,
      entityId: entitlement.id,
      entityNumber: entitlement.entitlementNumber,
      discountPercentage: customDiscount,
      discountValue: defaultTo(estimatedBill.response?.discretionaryDiscountAmount, 0),
    };
    let billEstimateError = null;
    if (customPromotions.length > 0 && !approvalRequired && isBillEsimationEnabled) {
      try {
        setEstimatedBill({ loading: true });
        const response = await getBillEstimate({
          transactionAccountId: defaultTo(entitlement?.transactionAccountId, ''),
          invoiceGroupId: defaultTo(entitlement?.billingGroupId, ''),
          entitlementId: entitlement.id,
          orderType: OrderItemType.AMENDMENT_ORDER,
          offeringId: entitlement?.offeringId ?? undefined,
          billingFrequency: entitlement?.billingFrequency as PricingPlanCycle,
          pricingPlanType: defaultPricePlan as PricingType,
          promotionIds: promotionIds,
          customPromotions: customPromotions,
          skipTrial: true,
        });
        entity.discountValue = defaultTo(response?.discretionaryDiscountAmount, 0);
        billEstimateError = null;
        setEstimatedBill({ response, loading: false });
      } catch (error: any) {
        billEstimateError = `Could not get the bill estimate due to an error "${error?.message}".`;
        setEstimatedBill({ error: billEstimateError, loading: false, response: null });
        return;
      }
    }

    if (approvalRequired) {
      submitRequestAfterApprovalCancelation(
        () =>
          submitApproval({
            approvalType: ApprovalType.ORDER_DISCOUNT,
            businessKey: entitlement.entitlementNumber as string,
            entityInformation: [entity],
            gsacId: formData.supportTicketNumber,
            initiatorComments: formData.comments,
            additionalInformation: requestData,
          }),
        hasOpenApprovalRequest
      );
    } else {
      if (!isDiscountApprovalFlowEnabled) {
        submitRewindOrder(requestData);
        return;
      }
      if (!discreteDiscountPromotions.length) {
        submitRequestAfterApprovalCancelation(() => submitRewindOrder(requestData), hasOpenApprovalRequest);
        return;
      }
      if (!billEstimateError) {
        const evaluation = await evaluateApproval([entity]);
        if (evaluation && !evaluation.isApprovalRequired && !evaluation.error) {
          submitRequestAfterApprovalCancelation(() => submitRewindOrder(requestData), hasOpenApprovalRequest);
        }
      }
    }
  };

  if (rewindOrderComplete && !notificationSent) {
    const transitionTimeError =
      rewindResponse.transitionTime === 'unknown'
        ? ' We were unable to determine if the successfully submitted order is immediate or scheduled.'
        : undefined;

    const notificationOptions = orderSuccessNotification(
      'REWIND_PLAN_V2',
      rewindResponse.transitionTime,
      transitionTimeError,
      true
    );
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    refetch();
  }

  const getDefaultBillingGroup = (invoiceGroupId: string) => {
    return availableInvoiceGroups?.filter((invoiceGroup: InvoiceGroup) => {
      return invoiceGroup.id === invoiceGroupId;
    });
  };

  const onOrderChange = async (orderItem: OrderSelectLineItem) => {
    const orderLineItemsLength = get(orderItem, 'order.orderLineItems.nodes.length', 0);
    if (orderLineItemsLength > 0) {
      //If there are multiple orderLineItems then filter lineItems based on orderItem entitlementId and select the first one,
      //else if no orderItem matches with entitlementId then select the first one from all available lineItems.
      // const filteredOrderLineItems = getFilteredOrderLineItem(orderItem);
      const orderLineItems = get(orderItem, 'order.orderLineItems.nodes', []);
      setSelectedOrderLineItem(orderLineItems[0]);
      let rewindOrderData;
      try {
        setRewindOrderDetails({ loading: true, data: null, error: null });
        rewindOrderData = await fetchRewindOrderDetails({
          orderItemId: get(orderLineItems, '0.lineItemId', ''),
          transactionAccountId: get(txaccount, 'id', ''),
        });
        setRewindOrderDetails({ loading: false, data: rewindOrderData, error: null });
      } catch (err: any) {
        const errorMessage = `Could not get the rewind to order due to an error - "${err?.message}". Please select another order to cancel.`;
        setRewindOrderDetails({
          loading: false,
          data: null,
          error: errorMessage,
        });

        return;
      }
      const validOrderOfferingId = get(rewindOrderData, 'item.offeringId', '');
      const validQuantity = get(
        rewindOrderData,
        'item.optedUsageOptions.chargingDetails.chargeQuantities[0].quantity',
        ''
      );
      setDefaultOfferingQuantity(validQuantity);
      const defaultOfferingOption =
        availableOfferingOptions?.find((offering: OfferingSelectItem) => offering.value.id === validOrderOfferingId) ??
        ({} as OfferingSelectItem);
      setDefaultOfferingItem(defaultOfferingOption);

      const defaultInvoiceGroupPricingPlan = billingGroupIdOnEntitlement
        ? getDefaultBillingGroup(billingGroupIdOnEntitlement)?.[0]?.shipToPriceEligibility
        : hasSingleInvoiceGroup
        ? availableInvoiceGroups?.[0]?.shipToPriceEligibility
        : undefined;
      setSelectedInvoiceGroupPricingPlan(defaultInvoiceGroupPricingPlan);
      onOfferingChange(defaultOfferingOption, defaultInvoiceGroupPricingPlan);
    }
  };

  const isStatuspageProduct = isStatuspage(entitlement?.productId);

  const onCustomPromotionChange = ({ discountPercentage }: PromotionItemDetail) => {
    // as only one discretionary discount is allowed, considering the last one in the stack
    setCustomDiscount(defaultTo(discountPercentage, 0));
  };

  return (
    <div className="entitlement-rewind-subscription">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title="Rewind subscription">
              <div className="sub-header">
                {!!entitlement?.productId && (
                  <EntitlementIcon productId={entitlement?.productId} isCloud={true} className="avatar medium" />
                )}
                <h4 className="sub-header--text" data-testid="productId">{`${
                  entitlement?.productName ?? 'Unnamed product'
                } ${entitlement?.entitlementNumber}`}</h4>
              </div>
            </FormHeader>
            <FormSection>
              {!!taskListError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to change the plan for the following reason"
                      message={taskListError.message}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {!!cancelApprovalRequestError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to change the plan for the following reason"
                      message={cancelApprovalRequestError.message}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {hasOpenApprovalRequest && (
                <InfoPanel
                  type="warning"
                  message={
                    <div className="approval-request-inflight-banner">
                      One or more approval requests are currently in progress. If you perform this action, those
                      approval requests will be canceled automatically.
                      {taskListData?.taskList?.tasks?.length && (
                        <TaskDetailAction
                          task={taskListData.taskList.tasks[0]}
                          text="View approval request details"
                          refetch={refetch}
                          refetchAll={refetch}
                        />
                      )}
                    </div>
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to change the plan for the following reason"
                      message={formError || 'Something went wrong.'}
                    />
                  }
                />
              )}
              <InfoPanel
                type="warning"
                message={
                  'The rewind to a previous subscription and recharge to the customer will be immediate. No refunds will be generated as part of this rewind. Refunds will first need to be requested from the invoice page.'
                }
              />
              <InflightInvoiceBanner
                transactionAccountId={entitlement.transactionAccountId as string}
                message={
                  'An invoice is currently being generated. Performing this action will cancel all orders and line items on that invoice. The subscription rewind will be immediate.'
                }
              />
              {!isEmpty(rewindOrderDetails?.data) && (
                <>
                  <br />
                  <InfoPanel type="information" message={InfoMessage.PRICING_PLAN_ELIGIBILITY_WARNING} />
                </>
              )}
            </FormSection>
            <FormSection>
              {!!approvalError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to change the plan for the following reason"
                      message={approvalError.message}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {!!estimatedBill.error && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to apply promotion for the following reason"
                      message={estimatedBill.error}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              <SelectOrderFormFieldV2
                fieldLabel={<h3 className="field-header">Select order to cancel</h3>}
                onChange={onOrderChange}
                orderLineItems={filteredOrderForRewind}
                isSearchable={false}
              />
              {filteredOrderForRewind && filteredOrderForRewind.length === 0 && (
                <ErrorMessage>There are no orders available to cancel.</ErrorMessage>
              )}
              {rewindOrderDetails?.error && (
                <ErrorMessage testId="rewind-detail-error">{rewindOrderDetails.error}</ErrorMessage>
              )}
              {invoiceData && (
                <div className="flex-container">
                  <div>
                    <label>Last bill date</label>
                    <div className="summary-item-value">
                      {invoiceData?.createdDate ? (
                        <FormattedDate date={invoiceData?.createdDate} format={NEW_DATE_FORMAT} />
                      ) : (
                        <div>NOT_AVAILABLE</div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label>Invoice number</label>
                    <div className="summary-item-value">
                      {invoiceData?.invoiceNumber ? (
                        <Link
                          to={`${INVOICE_PATH_2}/${encodeURIComponent(invoiceData?.invoiceNumber)}`}
                          data-testid="invoice-link"
                        >
                          {invoiceData?.invoiceNumber}
                        </Link>
                      ) : (
                        <div>NOT_AVAILABLE</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </FormSection>
            {rewindOrderDetails.loading ? (
              <div className="loading-spinner">
                <Spinner size="large" />
              </div>
            ) : (
              rewindOrderDetails?.data && (
                <FormSection>
                  <h3>Rewind to</h3>
                  <SummarySectionV2.SummarySection columns="200px 200px" className="rewind-subscription-summary">
                    <SummarySectionV2.SummaryGridCalloutItem testId="summary-item-plan">
                      {isStatuspageProduct ? (
                        <Field<ValueType<OfferingSelectItem>, HTMLSelectElement>
                          name="offeringSelectedItem"
                          defaultValue={defaultOfferingItem}
                          label="Plan"
                          isRequired
                        >
                          {({ fieldProps }) => (
                            <div data-testid="product-offering-select">
                              <Select<OfferingSelectItem>
                                {...fieldProps}
                                options={availableOfferingOptions}
                                isSearchable={false}
                                onChange={(selectedItem) => {
                                  if (selectedItem && selectedItem.value) {
                                    onOfferingChange(selectedItem, selectedInvoiceGroupPricingPlan);
                                    fieldProps.onChange(selectedItem);
                                  }
                                }}
                              />
                            </div>
                          )}
                        </Field>
                      ) : (
                        <div>
                          <label>Plan</label>
                          <div className="summary-item-value">{defaultOfferingItem?.label || NOT_AVAILABLE}</div>
                        </div>
                      )}
                    </SummarySectionV2.SummaryGridCalloutItem>
                    <SummarySectionV2.SummaryGridCalloutItem testId="summary-item-users">
                      {isStatuspageProduct ? (
                        <Field name="offeringQuantity" label="Tier" defaultValue={defaultOfferingQuantity} isRequired>
                          {({ fieldProps }) => (
                            <Textfield
                              {...fieldProps}
                              testId="offering-quantity"
                              isDisabled={isUserTierDisabled}
                              value={defaultOfferingQuantity}
                              onChange={(event: React.FormEvent<HTMLInputElement>) => {
                                setDefaultOfferingQuantity(event.currentTarget.value);
                                fieldProps.onChange(event);
                              }}
                              type="number"
                              min="1"
                              max="99"
                            />
                          )}
                        </Field>
                      ) : (
                        <div>
                          <label>User count</label>
                          <div className="summary-item-value">{defaultOfferingQuantity || NOT_AVAILABLE}</div>
                        </div>
                      )}
                    </SummarySectionV2.SummaryGridCalloutItem>
                    <SummarySectionV2.SummaryGridCalloutItem testId="summary-item-billing-cycle">
                      <div>
                        <label>Billing cycle</label>
                        <div className="summary-item-value">
                          <span>
                            {entitlement?.billingFrequency && entitlement?.billingFrequency !== 'OTHER'
                              ? `${capitalize(entitlement?.billingFrequency)} `
                              : NOT_AVAILABLE}
                          </span>
                        </div>
                      </div>
                    </SummarySectionV2.SummaryGridCalloutItem>
                    <SummarySectionV2.SummaryGridCalloutItem testId="summary-item-pricing">
                      <div>
                        <label>Pricing</label>
                        <div className="summary-item-value">{capitalize(defaultPricePlan) || NOT_AVAILABLE}</div>
                      </div>
                    </SummarySectionV2.SummaryGridCalloutItem>
                  </SummarySectionV2.SummarySection>
                </FormSection>
              )
            )}
            <FormSection>
              <SelectPromotionFormFieldV2
                fieldLabel="Add discount"
                availablePromotions={promotions}
                isDisabled={isEmpty(rewindOrderDetails?.data) || approvalRequired ? true : false}
                onCustomValueChange={onCustomPromotionChange}
              />
            </FormSection>
            <FormSection>
              <InvoiceGroupFormField
                fieldLabel="Billing profile"
                availableInvoiceGroups={getDefaultBillingGroup(billingGroupIdOnEntitlement)}
                entitlementBillingInvoiceGroupId={billingGroupIdOnEntitlement}
                isDisabled={true}
                maxWidth={null}
              />
            </FormSection>
            <FormSection>
              <Field
                name="supportTicketNumber"
                label={'Support ticket number'}
                isRequired={true}
                validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <Textfield
                      {...fieldProps}
                      name="supportTicketNumber"
                      placeholder={'Add GSAC ticket number'}
                      testId="support-ticket-number"
                    />
                    {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormSection>
              <Field label={'Internal comment'} name="handNote">
                {({ fieldProps }) => (
                  <Textfield {...fieldProps} name="handNote" testId="hand-note" placeholder={'Add comment'} />
                )}
              </Field>
            </FormSection>
            {approvalRequired ? (
              <ApprovalFormSection
                approvalType={ApprovalType.ORDER_DISCOUNT}
                slackChannel={approvalSlackChannel ?? ''}
                onCancel={() => closeSidebar()}
                isLoading={approvalProcessing || cancelApprovalRequestProcessing}
                approvalItems={approvalList.filter((item) => item.approvalRequired)}
                onEdit={setApprovalRequired}
              />
            ) : (
              <FormFooter align="end">
                <span className="form-footer-btn-wrapper">
                  <ButtonGroup>
                    <Button
                      appearance="subtle"
                      onClick={() => closeSidebar()}
                      testId="cancel"
                      className="close-sidebar-btn"
                    >
                      Close
                    </Button>
                    <LoadingButton
                      appearance={'warning'}
                      type="submit"
                      isLoading={processingOrder || approvalProcessing || estimatedBill.loading}
                      testId={'rewind-submit'}
                      aria-label="Rewind subscription"
                      isDisabled={
                        rewindOrderDetails.loading ||
                        !rewindOrderDetails?.data ||
                        !!approvalError ||
                        !!taskListError ||
                        !!estimatedBill.error
                      }
                    >
                      Rewind
                    </LoadingButton>
                  </ButtonGroup>
                </span>
              </FormFooter>
            )}
          </form>
        )}
      </Form>
    </div>
  );
};

export const EntitlementRewindAndRebillPane = withApollo(EntitlementRewindAndRebillPaneComponent);
